import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/layout/PageHeader";
import AnimatedNumber from "../components/common/AnimatedNumber";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import FlowDrilldown from "../components/dashboard/FlowDrilldown";
import styles from "./Dashboard.module.css";

const fetchSummary = async () => (await api.get("dashboard/summary")).data;

const TILES = [
  { key: "totalDeals",         label: "Deals",         icon: "bi-briefcase",    accent: "warning" },
  { key: "totalLoans",         label: "Loans",         icon: "bi-cash-stack",   accent: "primary" },
  { key: "totalNotes",         label: "Notes",         icon: "bi-journal-text", accent: "accent" },
  { key: "totalOrganizations", label: "Organizations", icon: "bi-buildings",    accent: "success" },
];

const Dashboard = () => {
  const { role } = useAuth();
  const navigate = useNavigate();
  const isAdmin = role === "admin";
  const [showFlow, setShowFlow] = useState(false);

  const summaryQ = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: fetchSummary,
  });

  const k = summaryQ.data?.kpis || {};
  const loading = summaryQ.isLoading;

  const NON_ADMIN_TARGETS = {
    totalDeals: null,                  // not visible to non-admin
    totalLoans: "/notes",
    totalNotes: "/notes",
    totalOrganizations: null,
  };

  const handleTileClick = (tile) => {
    if (isAdmin) { setShowFlow(true); return; }
    const target = NON_ADMIN_TARGETS[tile.key];
    if (target) navigate(target);
  };

  return (
    <div>
      <PageHeader pageName="Dashboard" />
      <div className="main-page-content fade-in">
        <div className={styles.headerRow}>
          <div>
            <h1 className={styles.title}>Welcome back</h1>
            <p className={styles.subtitle}>
              {isAdmin
                ? "Firm-wide overview. Click any tile to view details."
                : "Your organization at a glance."}
            </p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => summaryQ.refetch()}
            title="Refresh"
          >
            <i className="bi bi-arrow-clockwise" /> Refresh
          </button>
        </div>

        <div className={styles.kpiGrid}>
          {TILES.map((t) => {
            const interactive = isAdmin || !!NON_ADMIN_TARGETS[t.key];
            return (
              <KpiTile
                key={t.key}
                label={t.label}
                value={k[t.key] ?? 0}
                icon={t.icon}
                accent={t.accent}
                loading={loading}
                onClick={interactive ? () => handleTileClick(t) : null}
                ctaLabel={isAdmin ? "View details" : "Open"}
              />
            );
          })}
        </div>
      </div>

      {showFlow && <FlowDrilldown onClose={() => setShowFlow(false)} />}
    </div>
  );
};

const KpiTile = ({ label, value, icon, accent, loading, onClick, ctaLabel }) => {
  const interactive = !!onClick;
  return (
    <div
      className={`${styles.kpiTile} ${interactive ? styles.kpiTileLink : ""}`}
      data-accent={accent}
      onClick={interactive ? onClick : undefined}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => (e.key === "Enter" || e.key === " ") && onClick() : undefined}
    >
      <div className={styles.kpiRow}>
        <span className={styles.kpiLabel}>{label}</span>
        <span className={styles.kpiIcon}><i className={`bi ${icon}`} /></span>
      </div>
      <div className={styles.kpiValue}>
        {loading
          ? <span className={`skeleton ${styles.kpiSkel}`} />
          : <AnimatedNumber value={value} />}
      </div>
      {interactive && (
        <div className={styles.kpiFoot}>
          <span>{ctaLabel}</span>
          <i className="bi bi-arrow-right" />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
